import { useEffect } from 'react';
import { getStore, usePlayerStore } from '../../../stores/StoreContext';

/**
 * Custom hook that manages smart speed functionality for video playback.
 * Automatically adjusts playback speed based on proximity to event annotations,
 * using pre-computed speed ranges from the PlayerStore for optimal performance.
 * 
 * Smart Speed Logic:
 * - 10x speed by default
 * - 1x speed within 5 seconds before and 6 seconds after event annotations
 * - Automatically merges overlapping slow zones for seamless playback
 * - Uses pre-computed ranges to avoid expensive calculations on every time change
 * 
 * @returns {void} This hook doesn't return anything but manages playback speed
 * 
 * @example
 * // Use in any component that needs smart speed control
 * import { useSmartSpeed } from '../hooks/useSmartSpeed';
 * 
 * const MyComponent = () => {
 *   useSmartSpeed();
 *   return <div>Component content</div>;
 * };
 */
export const useSmartSpeed = () => {
  const PlayerStore = getStore('playerStore');
  const currentTime = usePlayerStore((state) => state.currentTime);
  const smartSpeedEnabled = usePlayerStore((state) => state.smartSpeedEnabled);
  const smartSpeedPlaybackRanges = usePlayerStore((state) => state.smartSpeedPlaybackRanges);
  const suggestedPlaybackSpeed = usePlayerStore((state) => state.suggestedPlaybackSpeed);

  // Apply speed changes based on current time and pre-computed ranges
  useEffect(() => {
    if (!smartSpeedEnabled) {
      return; // Smart speed is disabled, no action needed
    }

    if (smartSpeedPlaybackRanges.length === 0) {
      return; // Ranges not calculated yet
    }

    // Find the current speed range using efficient lookup
    const currentRange = smartSpeedPlaybackRanges.find(range => 
      currentTime >= range.start && currentTime < range.end
    );

    if (currentRange) {
      // use the suggested playback speed...

      let speed = currentRange.speed;
      if (suggestedPlaybackSpeed && !currentRange?.normal) {
        speed = suggestedPlaybackSpeed;
      }

      PlayerStore.setPlaybackSpeed(speed, true);
    }

  }, [smartSpeedEnabled, currentTime, smartSpeedPlaybackRanges, PlayerStore]);
};

export default useSmartSpeed;
